import { isSafeUrl } from '@atlaskit/adf-schema/is-safe-url';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';
import type { SMART_LINK_TYPE } from '@atlaskit/editor-common/analytics/types/node-events';
import type { EmbedCardTransformAttrs } from '@atlaskit/editor-common/card/types';
import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions/manifest-helpers';
import { processRawValue } from '@atlaskit/editor-common/process-raw-value';
import type { Command } from '@atlaskit/editor-common/types/command';
import type {
	OptionalPlugin,
	PublicPluginAPI,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import type { CardPlugin } from '@atlaskit/editor-plugin-card/cardPluginType';
import type { Node, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { NATIVE_EMBED_PARAMETER_DEFAULTS } from '@atlaskit/native-embeds-common/constants';
import type {
	CardToNativeEmbedTransformOptions,
	NativeEmbedLayout,
} from '@atlaskit/native-embeds-common/plugin-actions/types';
import type { AlignmentValue } from '@atlaskit/native-embeds-common/types';
import { setParameters } from '@atlaskit/native-embeds-common/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';
import type { ExperienceManifest } from '@atlassian/native-embeds-core/types/experience/types';

type NativeEmbedCardTransformAttrs = EmbedCardTransformAttrs & {
	/**
	 * Preserve the source embedCard identity when it is converted into a native
	 * embed extension. This is intentionally scoped to native-embeds so we do not
	 * widen the shared editor-common transform contract for all card consumers.
	 */
	localId?: string;
};

const getTransformLocalId = (attrs: EmbedCardTransformAttrs): string | undefined =>
	(attrs as NativeEmbedCardTransformAttrs).localId;

const LAYOUT_TO_ALIGNMENT: Record<NativeEmbedLayout, AlignmentValue> = {
	center: 'center',
	'full-width': 'center',
	wide: 'center',
	'wrap-left': 'wrap-left',
	'wrap-right': 'wrap-right',
	'align-start': 'left',
	'align-end': 'right',
};

function mapLayoutToAlignment(layout: string = 'center'): AlignmentValue {
	return LAYOUT_TO_ALIGNMENT[layout as NativeEmbedLayout] ?? 'center';
}

/**
 * Transforms native embed ADF attrs into a native embed extension Node when the URL
 * is supported by a registered experience.
 *
 * Returns undefined if:
 * - cc-maui-experiment and jira_maui_remix are both disabled
 * - URL is not safe
 * - URL is not supported by any registered experience
 */
export function cardToNativeEmbedNode(
	schema: Schema,
	attrs: EmbedCardTransformAttrs,
	api: PublicPluginAPI<[OptionalPlugin<CardPlugin>]>,
): Node | undefined | Promise<Node | undefined>;
export function cardToNativeEmbedNode(
	schema: Schema,
	attrs: EmbedCardTransformAttrs,
	api?: undefined,
): Node | undefined;
export function cardToNativeEmbedNode(
	schema: Schema,
	attrs: EmbedCardTransformAttrs,
	api?: PublicPluginAPI<[OptionalPlugin<CardPlugin>]>,
): Node | undefined | Promise<Node | undefined> {
	if (
		!expVal('cc-maui-experiment', 'isEnabled', false) &&
		!fg('platform_native_embeds_rollout_non_maui_experience')
	) {
		return undefined;
	}

	const { url, layout = 'center', originalHeight, originalWidth } = attrs;

	if (!isSafeUrl(url)) {
		return undefined;
	}

	const facade = getNativeEmbedFacade();

	// Fast path: synchronous manifest lookup for regular (non-short-link) URLs.
	const manifest = facade.getManifestByUrl(url);
	if (manifest) {
		return buildNativeEmbedNode(
			schema,
			{
				url,
				layout,
				originalHeight,
				originalWidth,
				localId: getTransformLocalId(attrs),
			},
			manifest,
		);
	}

	// Async path: for Confluence short-link URLs (/wiki/x/<token>), expand via ORS
	// and retry the manifest lookup with the resolved canonical URL.
	// Uses api.card.actions.resolveShortLinkUrl which accesses the card plugin's
	// internal CardClient — the same one used by all smart cards in the editor.
	if (
		api?.card?.actions?.resolveShortLinkUrl &&
		fg('platform_native_embeds_rollout_non_maui_experience')
	) {
		return api.card.actions.resolveShortLinkUrl(url).then((resolvedUrl) => {
			if (!resolvedUrl) {
				return undefined;
			}
			const resolvedManifest = facade.getManifestByUrl(resolvedUrl);
			if (!resolvedManifest) {
				return undefined;
			}
			return buildNativeEmbedNode(
				schema,
				{
					url: resolvedUrl,
					layout,
					originalHeight,
					originalWidth,
					localId: getTransformLocalId(attrs),
				},
				resolvedManifest,
			);
		});
	}

	return undefined;
}

function buildNativeEmbedNode(
	schema: Schema,
	attrs: NativeEmbedCardTransformAttrs,
	manifest: ExperienceManifest,
): Node | undefined {
	const { url, layout = 'center', originalHeight, originalWidth, localId } = attrs;

	const hasOriginalDimensions = !!(originalWidth && originalHeight);
	const width = originalWidth ?? NATIVE_EMBED_PARAMETER_DEFAULTS.width;

	// Use aspectRatio when we have computed dimensions, or when a width is set/inferred
	// (so the renderer can derive height). Only fall back to a fixed height when there
	// is no width at all, because aspectRatio is meaningless without a width.
	const hasWidth = !!width;

	const { height: _defaultHeight, ...defaultsWithoutHeight } = NATIVE_EMBED_PARAMETER_DEFAULTS;
	const macroParams = setParameters(
		{},
		{
			...(hasWidth ? defaultsWithoutHeight : NATIVE_EMBED_PARAMETER_DEFAULTS),
			url,
			alignment: mapLayoutToAlignment(layout),
			width,
			aspectRatio: hasOriginalDimensions
				? originalWidth / originalHeight
				: hasWidth
					? NATIVE_EMBED_PARAMETER_DEFAULTS.aspectRatio
					: undefined,
			// height is only stored when there is no width (aspectRatio cannot be used without one)
			...(hasWidth ? {} : { height: NATIVE_EMBED_PARAMETER_DEFAULTS.height }),
		},
	);

	const extensionAdf: ADFEntity = {
		type: 'extension',
		attrs: {
			extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:${manifest.id}`,
			extensionType: NATIVE_EMBED_EXTENSION_TYPE,
			...(localId ? { localId } : {}),
			parameters: {
				...macroParams,
				macroMetadata: {
					// Preserved data in case of revert.
					url,
					layout,
					originalHeight: originalHeight ?? undefined,
					originalWidth: originalWidth ?? undefined,
				},
			},
		},
	};

	return processRawValue(schema, extensionAdf);
}

/**
 * Converts inserted embed or linked content into a native embed node when the URL is supported.
 */
export function insertedContentToNativeEmbedNode(schema: Schema, node: Node): Node | undefined {
	if (node.type === schema.nodes.embedCard) {
		return cardToNativeEmbedNode(schema, {
			url: node.attrs.url,
			layout: node.attrs.layout,
			width: node.attrs.width,
			originalHeight: node.attrs.originalHeight,
			originalWidth: node.attrs.originalWidth,
			localId: node.attrs.localId,
		} as NativeEmbedCardTransformAttrs);
	}

	if (
		node.type === schema.nodes.paragraph &&
		node.childCount === 1 &&
		node.firstChild?.isText &&
		node.firstChild.marks.length
	) {
		const linkMarkType = schema.marks.link;
		const linkMark = linkMarkType
			? node.firstChild.marks.find((mark) => mark.type === linkMarkType)
			: undefined;
		const url = linkMark?.attrs.href;
		if (url) {
			return cardToNativeEmbedNode(schema, {
				url,
				layout: 'center',
				width: undefined,
			});
		}
	}

	return undefined;
}

function appearanceForNodeType(schema: Schema, nodeType: NodeType) {
	if (nodeType === schema.nodes.inlineCard) {
		return 'inline';
	}
	if (nodeType === schema.nodes.blockCard) {
		return 'block';
	}
	if (nodeType === schema.nodes.embedCard) {
		return 'embed';
	}
	return 'url';
}

/**
 * Creates a Command that replaces a selected inline/block card with a native embed
 * when the URL is supported.
 *
 * Handles document structure: replaceWith, trailing space insertion, selection,
 * and preceding paragraph deletion.
 *
 * Use augmentTransaction to add card-plugin concerns (datasource stash, analytics,
 * link metadata).
 *
 * Returns false if no node selected, URL not supported, or not an inline/block card.
 */
export function createCardToNativeEmbedTransform(
	options?: CardToNativeEmbedTransformOptions,
): Command {
	if (
		!expVal('cc-maui-experiment', 'isEnabled', false) &&
		!fg('platform_native_embeds_rollout_non_maui_experience')
	) {
		return () => false;
	}

	const { augmentTransaction, editorAnalyticsApi } = options ?? {};

	return (state, dispatch) => {
		const selectedNode =
			state.selection instanceof NodeSelection ? state.selection.node : undefined;
		if (!selectedNode) {
			return false;
		}

		const url = selectedNode.attrs.url ?? selectedNode.attrs.data?.url;
		if (!url) {
			return false;
		}

		const previousType = appearanceForNodeType(state.schema, selectedNode.type) as SMART_LINK_TYPE;

		const embedAttrs: NativeEmbedCardTransformAttrs = {
			url,
			layout: selectedNode.attrs.layout ?? 'center',
			width: selectedNode.attrs.width ?? NATIVE_EMBED_PARAMETER_DEFAULTS.width,
			originalHeight: selectedNode.attrs.originalHeight ?? NATIVE_EMBED_PARAMETER_DEFAULTS.height,
			originalWidth: selectedNode.attrs.originalWidth ?? NATIVE_EMBED_PARAMETER_DEFAULTS.width,
			localId: selectedNode.attrs.localId,
		};

		const extensionNode = cardToNativeEmbedNode(state.schema, embedAttrs);
		if (!extensionNode) {
			return false;
		}

		const { from, to } = state.selection;
		const tr = state.tr.replaceWith(from, to, extensionNode);

		if (tr.doc.nodeSize - 2 === to) {
			tr.insertText(' ', to);
		}

		tr.setSelection(TextSelection.create(tr.doc, to + 1));

		const previousNodePos = from - 1 > 0 ? from - 1 : 0;
		const previousNode = tr.doc.nodeAt(previousNodePos);
		if (previousNode?.type?.name === 'paragraph') {
			tr.delete(previousNodePos, from);
		}

		augmentTransaction?.(tr, state);

		if (dispatch) {
			editorAnalyticsApi?.attachAnalyticsEvent({
				action: ACTION.CHANGED_TYPE,
				actionSubject: ACTION_SUBJECT.NATIVE_EMBED,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					newType: 'nativeEmbed',
					previousType,
				},
			})(tr);
			dispatch(tr.scrollIntoView());
		}
		return true;
	};
}
